import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { WorkspaceView } from "../facilities/drilldown/nav-state.js";
import { WORKSPACE_ENTRIES } from "../workspace/workspace-pages.js";

/**
 * One legacy "overview window" (ADR-033 §1): a titled card whose arrow opens the dashboard page it summarises — a page
 * switch INSIDE the workspace (replace, ADR-032's history model). The title is the card's heading; the arrow carries
 * "Open <page>" for assistive tech.
 */
export function OverviewCard({ page, title, onOpen, children }: { page: WorkspaceView; title: string; onOpen: (page: WorkspaceView) => void; children: ReactNode }) {
  const { t } = useTranslation();
  const entry = WORKSPACE_ENTRIES.find((e) => e.id === page);
  const headingId = `overview-${page}-heading`;
  return (
    <section
      aria-labelledby={headingId}
      className="border-border/60 bg-background text-foreground hover:border-ring/40 flex min-w-0 flex-col rounded-xl border shadow-xs transition-[box-shadow,border-color] duration-150 ease-out hover:shadow-md motion-reduce:transition-none dark:border-border/50"
    >
      {/* The editor's card head: a tinted strip, so the card reads as head + content rather than one slab (design pass 2026-11-04). */}
      <div className="border-border/60 bg-card/60 flex items-center justify-between gap-2 rounded-t-xl border-b px-4 py-2 dark:bg-black/20">
        <h2 id={headingId} className="text-sm font-semibold">
          {title}
        </h2>
        <Button variant="ghost" size="icon-sm" aria-label={t("dashboard.openNamed", { name: entry ? t(entry.labelKey) : title })} onPress={() => onOpen(page)}>
          <ArrowRight aria-hidden="true" className="size-4" />
        </Button>
      </div>
      <div className="flex flex-1 flex-col gap-3 px-4 py-3">{children}</div>
    </section>
  );
}
